import React, { useState, useEffect, useRef, useCallback } from 'react';
import NavelDayContent from './NavelDayContent';
import NavelSidebar from './NavelSidebar';
import './navel.css';

const GOOGLE_TRANSLATE_API_KEY = process.env.REACT_APP_GOOGLE_TRANSLATE_API_KEY;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const OPENWEATHER_API_KEY = process.env.REACT_APP_OPENWEATHER_API_KEY;

function HeroCountdown({ departureDateTime }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  if (!departureDateTime) return null;
  const dep = new Date(departureDateTime);
  if (isNaN(dep.getTime())) return null;
  const diff = dep - now;
  if (diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return (
    <div className="navel-countdown">
      <div className="navel-countdown-label">Takeoff in</div>
      <div className="navel-countdown-units">
        <div className="navel-countdown-unit"><span className="navel-countdown-num">{d}</span><span className="navel-countdown-sub">days</span></div>
        <div className="navel-countdown-unit"><span className="navel-countdown-num">{String(h).padStart(2,'0')}</span><span className="navel-countdown-sub">hrs</span></div>
        <div className="navel-countdown-unit"><span className="navel-countdown-num">{String(m).padStart(2,'0')}</span><span className="navel-countdown-sub">min</span></div>
        <div className="navel-countdown-unit"><span className="navel-countdown-num">{String(s).padStart(2,'0')}</span><span className="navel-countdown-sub">sec</span></div>
      </div>
    </div>
  );
}

const getLocalDate = (tz) => {
  const now = new Date();
  return new Date(now.toLocaleString('en-US', { timeZone: tz })).toISOString().split('T')[0];
};
const isDayPast = (d, tz) => d < getLocalDate(tz);

/* ---- Inline Weather ---- */
function NavelWeather({ weatherData, weatherLoading }) {
  if (weatherLoading || !weatherData || weatherData.length === 0) return null;
  return (
    <div style={{ marginBottom: 20 }}>
      <p className="navel-label" style={{ marginBottom: 8 }}>Weather</p>
      <div className="navel-weather">
        {weatherData.map((hour, idx) => {
          const time = new Date(hour.dt * 1000);
          return (
            <div key={idx} className="navel-weather-hour">
              <div className="navel-small" style={{ marginBottom: 2 }}>{time.getHours()}:00</div>
              <div style={{ fontSize: '1.25rem', margin: '4px 0' }}>
                {hour.weather[0].main === 'Clear' ? '☀️' :
                 hour.weather[0].main === 'Clouds' ? '☁️' :
                 hour.weather[0].main === 'Rain' ? '🌧️' : '🌤️'}
              </div>
              <div style={{ fontWeight: 700, fontSize: '0.8125rem', color: 'var(--text-primary)' }}>{Math.round(hour.main.temp)}°</div>
              {hour.pop > 0.3 && <div style={{ fontSize: '0.625rem', color: 'var(--color-accent)', marginTop: 2 }}>{Math.round(hour.pop * 100)}%</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function NavelTripApp({ tripConfig }) {
  const { translation, currency, days } = tripConfig;

  // Theme
  const [theme, setTheme] = useState('dark');

  // Override body background for Navel
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = theme === 'dark' ? '#0D1B19' : '#F5F0E8';
    return () => { document.body.style.backgroundColor = prev; };
  }, [theme]);

  // UI state
  const [scrolled, setScrolled] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [activeSection, setActiveSection] = useState(days[0]?.id || '');
  const [showAllDays, setShowAllDays] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [activeDayTab, setActiveDayTab] = useState({}); // per-day tab state

  // Sidebar
  const [sidebarTab, setSidebarTab] = useState('tools');

  // Weather
  const [weatherData, setWeatherData] = useState({});
  const [weatherLoading, setWeatherLoading] = useState(true);

  // Translation
  const [translateFrom, setTranslateFrom] = useState('');
  const [translateTo, setTranslateTo] = useState('');
  const [translating, setTranslating] = useState(false);
  const defaultDirection = `${translation.sourceLang}-${translation.targetLang}`;
  const reverseDirection = `${translation.targetLang}-${translation.sourceLang}`;
  const [translateDirection, setTranslateDirection] = useState(defaultDirection);

  // Currency
  const [homeAmount, setHomeAmount] = useState(currency.defaultHomeAmount);
  const [localAmount, setLocalAmount] = useState(currency.defaultLocalAmount);
  const [exchangeRate, setExchangeRate] = useState(currency.defaultRate);
  const [rateLoading, setRateLoading] = useState(true);

  // Maps
  const [mapsLoaded, setMapsLoaded] = useState(false);

  // Activities
  const storageKey = `completedActivities_${tripConfig.id}`;
  const [completedActivities, setCompletedActivities] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved ? JSON.parse(saved) : {};
  });

  // Refs
  const heroRef = useRef(null);
  const heroImgRef = useRef(null);

  const visibleDays = showAllDays ? days : days.filter(day => !isDayPast(day.date, tripConfig.timezone));
  const completedDaysCount = days.length - days.filter(day => !isDayPast(day.date, tripConfig.timezone)).length;
  const tripComplete = visibleDays.length === 0 && !showAllDays;

  // ---- Collect day thumbnails for the circular row ----
  const getDayThumb = (dayKey) => {
    const img = tripConfig.images.dayImages[dayKey];
    if (!img) return null;
    if (Array.isArray(img)) return img[0];
    return img;
  };

  // ---- Effects ----
  useEffect(() => {
    const fetchRate = async () => {
      try {
        const res = await fetch(`https://api.exchangerate-api.com/v4/latest/${currency.home.code}`);
        const data = await res.json();
        if (data.rates?.[currency.local.code]) {
          const rate = data.rates[currency.local.code];
          setExchangeRate(rate);
          setLocalAmount((parseFloat(homeAmount) * rate).toFixed(0));
        }
      } catch (e) { console.error(e); }
      setRateLoading(false);
    };
    fetchRate();
  }, [currency.home.code, currency.local.code]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(completedActivities));
  }, [completedActivities, storageKey]);

  // Lazy-load Google Maps only when a Location tab is opened
  const loadMapsIfNeeded = useCallback(() => {
    if (mapsLoaded) return;
    if (window.google) { setMapsLoaded(true); return; }
    if (!GOOGLE_MAPS_API_KEY) return;
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    s.async = true; s.defer = true;
    s.onload = () => setMapsLoaded(true);
    s.onerror = () => console.warn('Google Maps failed to load');
    document.head.appendChild(s);
  }, [mapsLoaded]);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const res = await fetch(
          `https://api.openweathermap.org/data/2.5/forecast?lat=${tripConfig.coordinates.lat}&lon=${tripConfig.coordinates.lng}&appid=${OPENWEATHER_API_KEY}&units=metric`
        );
        setWeatherData({ [tripConfig.city]: await res.json() });
      } catch (e) { console.error(e); }
      setWeatherLoading(false);
    };
    fetchWeather();
  }, [tripConfig.coordinates.lat, tripConfig.coordinates.lng, tripConfig.city]);

  // When Location tab is activated, load Maps and initialize
  useEffect(() => {
    const anyMapTabOpen = visibleDays.some(day => (activeDayTab[day.id] || 'itinerary') === 'map');
    if (anyMapTabOpen) {
      loadMapsIfNeeded();
    }
    if (mapsLoaded) {
      visibleDays.forEach(day => {
        if ((activeDayTab[day.id] || 'itinerary') === 'map') {
          setTimeout(() => initializeMap(day), 200);
        }
      });
    }
  }, [mapsLoaded, visibleDays.length, theme, activeDayTab, loadMapsIfNeeded]);

  // Scroll handler — parallax + section tracking
  useEffect(() => {
    const onScroll = () => {
      const sy = window.scrollY;
      setScrolled(sy > 60);
      setShowBackToTop(sy > 500);

      // Parallax
      if (heroImgRef.current && heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        if (rect.bottom > 0) {
          heroImgRef.current.style.transform = `translateY(${sy * 0.3}px) scale(1.15)`;
        }
      }

      // Track active section
      let current = visibleDays[0]?.id || '';
      for (const day of visibleDays) {
        const el = document.getElementById(day.id);
        if (el) {
          const r = el.getBoundingClientRect();
          if (r.top <= 160 && r.bottom >= 160) { current = day.id; break; }
        }
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [visibleDays.length]);

  // ---- Handlers ----
  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 40, behavior: 'smooth' });
  }, []);

  const toggleActivity = (id) => setCompletedActivities(prev => ({ ...prev, [id]: !prev[id] }));
  const handlePrint = () => window.print();

  const initializeMap = (day) => {
    const el = document.getElementById(`map-${day.id}`);
    if (!el || !window.google) return;
    const map = new window.google.maps.Map(el, {
      zoom: 13,
      center: day.locations[0] ? { lat: day.locations[0].lat, lng: day.locations[0].lng } : { lat: tripConfig.coordinates.lat, lng: tripConfig.coordinates.lng },
      styles: theme === 'dark' ? darkMapStyle : lightMapStyle,
      disableDefaultUI: true,
      zoomControl: true,
    });
    const bounds = new window.google.maps.LatLngBounds();
    day.locations.forEach(loc => {
      const marker = new window.google.maps.Marker({
        position: { lat: loc.lat, lng: loc.lng }, map, title: loc.name,
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: '#D4A574', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 }
      });
      const iw = new window.google.maps.InfoWindow({
        content: `<div style="padding:4px 8px;font-family:'Plus Jakarta Sans',sans-serif;font-size:13px"><strong>${loc.name}</strong></div>`
      });
      marker.addListener('click', () => iw.open(map, marker));
      bounds.extend(marker.getPosition());
    });
    if (day.locations.length > 1) map.fitBounds(bounds, 40);
  };

  // Translation
  const handleTranslate = async () => {
    if (!translateFrom.trim()) return;
    setTranslating(true);
    try {
      const [sLang, tLang] = translateDirection === defaultDirection
        ? [translation.sourceLang, translation.targetLang]
        : [translation.targetLang, translation.sourceLang];
      const res = await fetch(
        `https://translation.googleapis.com/language/translate/v2?key=${GOOGLE_TRANSLATE_API_KEY}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ q: translateFrom, source: sLang, target: tLang, format: 'text' }) }
      );
      const data = await res.json();
      if (data.data?.translations?.[0]) setTranslateTo(data.data.translations[0].translatedText);
    } catch { setTranslateTo('Translation error.'); }
    setTranslating(false);
  };

  useEffect(() => {
    const t = setTimeout(() => { if (translateFrom.trim()) handleTranslate(); }, 500);
    return () => clearTimeout(t);
  }, [translateFrom, translateDirection]);

  const swapLanguages = () => {
    setTranslateDirection(p => p === defaultDirection ? reverseDirection : defaultDirection);
    setTranslateFrom(translateTo);
    setTranslateTo(translateFrom);
  };
  const handleHomeChange = (v) => { setHomeAmount(v); setLocalAmount(((parseFloat(v) || 0) * exchangeRate).toFixed(0)); };
  const handleLocalChange = (v) => { setLocalAmount(v); setHomeAmount(((parseFloat(v) || 0) / exchangeRate).toFixed(2)); };
  const speakText = (text, lang) => { if ('speechSynthesis' in window) { const u = new SpeechSynthesisUtterance(text); u.lang = lang; window.speechSynthesis.speak(u); } };
  const copyToClipboard = (text) => navigator.clipboard.writeText(text);
  const openGoogleLens = () => { window.location.href = 'google-lens://'; };

  const getDayWeather = (date) => {
    const cw = weatherData[tripConfig.city];
    if (!cw?.list) return [];
    return cw.list.filter(i => {
      const d = new Date(i.dt * 1000);
      return d.toISOString().split('T')[0] === date && d.getHours() >= 10 && d.getHours() <= 23;
    }).slice(0, 5);
  };

  const sidebarProps = {
    tripConfig, sidebarTab, setSidebarTab,
    translateFrom, setTranslateFrom,
    translateTo, translateDirection, translating,
    swapLanguages, speakText, copyToClipboard,
    homeAmount, localAmount, handleHomeChange, handleLocalChange,
    exchangeRate, rateLoading, openGoogleLens, handlePrint
  };

  return (
    <div className="navel-app" data-theme={theme}>

      {/* ======== TOP BAR ======== */}
      <div className={`navel-top-bar no-print ${scrolled ? 'scrolled' : ''}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontFamily: 'var(--font-heading)', fontSize: '1.125rem', color: 'var(--text-primary)', opacity: scrolled ? 1 : 0, transition: 'opacity 0.3s' }}>
            {tripConfig.city}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {completedDaysCount > 0 && (
            <button onClick={() => setShowAllDays(!showAllDays)} className="navel-btn-secondary" style={{ padding: '5px 14px', fontSize: '0.6875rem' }}>
              {showAllDays ? 'Current' : 'All Days'}
            </button>
          )}
          <button className="navel-theme-toggle" onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} aria-label="Toggle theme" />
          {/* Mobile tools trigger */}
          <button className="navel-btn-icon" onClick={() => setMobileSidebarOpen(true)} aria-label="Tools"
            style={{ width: 36, height: 36, fontSize: '0.875rem', display: 'none' }} id="mobile-tools-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
            </svg>
          </button>
        </div>
        <style>{`@media(max-width:768px){#mobile-tools-btn{display:flex!important}}`}</style>
      </div>

      {/* ======== HERO ======== */}
      <div className="navel-hero" ref={heroRef}>
        <img
          ref={heroImgRef}
          src={tripConfig.images.banner}
          alt={tripConfig.images.bannerAlt}
          className="navel-hero-img"
          style={{ transform: 'scale(1.15)' }}
          onError={(e) => { e.target.src = `https://placehold.co/1920x700/162422/D4A574?text=${tripConfig.images.bannerFallback || tripConfig.city}`; }}
        />
        <HeroCountdown departureDateTime={tripConfig.flights?.outbound?.takeoffAt} />
      </div>

      {/* ======== MAIN GRID ======== */}
      <div className="navel-content-grid">
        <main>
          {tripComplete ? (
            <div className="navel-trip-complete">
              <h2 className="navel-h1" style={{ marginBottom: 16 }}>Trip Complete</h2>
              <p className="navel-body" style={{ marginBottom: 32 }}>{tripConfig.farewell}</p>
              <button onClick={() => setShowAllDays(true)} className="navel-btn-primary">View Full Itinerary</button>
            </div>
          ) : (
            <>
              {completedDaysCount > 0 && !showAllDays && (
                <p className="navel-small" style={{ paddingTop: 16, marginBottom: 0 }}>
                  {completedDaysCount} {completedDaysCount === 1 ? 'day' : 'days'} completed
                </p>
              )}

              {visibleDays.map((day, index) => {
                const dayTabKey = day.id;
                const currentTab = activeDayTab[dayTabKey] || 'itinerary';

                return (
                  <React.Fragment key={day.id}>
                    {index > 0 && (
                      <div className="navel-day-divider">
                        <span className="navel-day-divider-icon">✦</span>
                      </div>
                    )}
                    <section id={day.id} className="navel-day-section" style={isDayPast(day.date, tripConfig.timezone) ? { opacity: 0.5 } : {}}>

                      {/* Day Badge + Title */}
                      <span className="navel-day-badge">Day {day.dayNum}</span>
                      <h2 className="navel-h2" style={{ marginBottom: 6 }}>{day.title}</h2>

                      <div className="navel-location" style={{ marginBottom: 16 }}>
                        <span className="navel-location-dot" />
                        {new Date(day.date + 'T12:00:00').toLocaleDateString('en-GB', {
                          weekday: 'long', day: 'numeric', month: 'long'
                        })}
                        {day.walkingEstimate && <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>~{day.walkingEstimate}</span>}
                      </div>

                      {/* ---- Content Tabs (History / Location / Discover pattern) ---- */}
                      <div className="navel-content-tabs">
                        {[
                          { key: 'itinerary', label: 'Itinerary' },
                          { key: 'map', label: 'Location' },
                          { key: 'checklist', label: 'Checklist' },
                        ].map(tab => (
                          <button
                            key={tab.key}
                            className={`navel-content-tab ${currentTab === tab.key ? 'active' : ''}`}
                            onClick={() => setActiveDayTab(prev => ({ ...prev, [dayTabKey]: tab.key }))}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>

                      {/* Tab: Itinerary */}
                      {currentTab === 'itinerary' && (
                        <>
                          <NavelWeather weatherData={getDayWeather(day.date)} weatherLoading={weatherLoading} />
                          <NavelDayContent day={day} tripConfig={tripConfig} copyToClipboard={copyToClipboard} />
                        </>
                      )}

                      {/* Tab: Location */}
                      {currentTab === 'map' && (
                        <div>
                          {day.locations.length > 0 ? (
                            <>
                              <p className="navel-label" style={{ marginBottom: 16 }}>Today's Locations</p>
                              {day.locations.map((loc, li) => (
                                <a
                                  key={li}
                                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.name + ' ' + (loc.address || tripConfig.city))}`}
                                  target="_blank" rel="noopener noreferrer"
                                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 0', borderBottom: '1px solid var(--border-subtle)', textDecoration: 'none', cursor: 'pointer' }}
                                >
                                  <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-sm)', background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.875rem' }}>
                                    {loc.type === 'restaurant' ? '🍽' : loc.type === 'bar' ? '🍸' : loc.type === 'beach' ? '🏖' : loc.type === 'accommodation' ? '🏠' : loc.type === 'station' ? '🚂' : '📍'}
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>{loc.name}</p>
                                    <p className="navel-small">{loc.address || loc.type}</p>
                                  </div>
                                  <span style={{ color: 'var(--color-accent)', fontSize: '0.75rem', fontWeight: 600 }}>Open →</span>
                                </a>
                              ))}
                            </>
                          ) : (
                            <p className="navel-body" style={{ fontStyle: 'italic', padding: '24px 0' }}>Locations depend on which route you choose — check the Itinerary tab.</p>
                          )}
                        </div>
                      )}

                      {/* Tab: Checklist */}
                      {currentTab === 'checklist' && (
                        <div className="navel-checklist">
                          <p className="navel-label" style={{ marginBottom: 12 }}>Pack & Prepare</p>
                          {(day.checklist || []).map(item => (
                            <div key={item.id} className={`navel-check-item ${completedActivities[item.id] ? 'completed' : ''}`}>
                              <input
                                type="checkbox" id={item.id}
                                checked={completedActivities[item.id] || false}
                                onChange={() => toggleActivity(item.id)}
                              />
                              <label htmlFor={item.id} style={{ fontSize: '0.875rem', cursor: 'pointer', flex: 1, color: 'var(--text-secondary)' }}>
                                {item.name}
                              </label>
                            </div>
                          ))}
                          {(!day.checklist || day.checklist.length === 0) && (
                            <p className="navel-body" style={{ fontStyle: 'italic' }}>No checklist for this day.</p>
                          )}
                        </div>
                      )}

                    </section>
                  </React.Fragment>
                );
              })}
            </>
          )}
        </main>

        {/* ======== DESKTOP SIDEBAR ======== */}
        <aside className="no-print" style={{ display: 'none' }} id="navel-desktop-sidebar">
          <div style={{ position: 'sticky', top: 80 }}>
            <NavelSidebar {...sidebarProps} />
          </div>
        </aside>
        <style>{`@media(min-width:769px){#navel-desktop-sidebar{display:block!important}}`}</style>
      </div>

      {/* ======== FOOTER ======== */}
      <footer className="no-print" style={{ borderTop: '1px solid var(--border-subtle)', padding: '32px 24px 100px', textAlign: 'center' }}>
        <p className="navel-small">{tripConfig.footer}</p>
      </footer>

      {/* ======== FLOATING BOTTOM NAV ======== */}
      <div className={`navel-bottom-nav navel-glass-heavy no-print ${!showBackToTop && visibleDays.length <= 1 ? 'hidden' : ''}`}>
        {visibleDays.map(day => (
          <button
            key={day.id}
            className={`navel-bottom-nav-label ${activeSection === day.id ? 'active' : ''}`}
            onClick={() => scrollTo(day.id)}
          >
            Day {day.dayNum}
          </button>
        ))}
        {/* Mobile: info button in nav */}
        <button className="navel-bottom-nav-label" onClick={() => setMobileSidebarOpen(true)}
          style={{ display: 'none' }} id="nav-info-btn-mobile">
          Info
        </button>
        <style>{`@media(max-width:768px){#nav-info-btn-mobile{display:block!important}}`}</style>
      </div>

      {/* ======== MOBILE BOTTOM SHEET ======== */}
      <div className={`navel-bottom-sheet-overlay ${mobileSidebarOpen ? 'open' : ''}`} onClick={() => setMobileSidebarOpen(false)} />
      <div className={`navel-bottom-sheet ${mobileSidebarOpen ? 'open' : ''}`}>
        <div className="navel-bottom-sheet-handle" />
        <NavelSidebar {...sidebarProps} />
      </div>

      {/* ======== BACK TO TOP ======== */}
      <div className={`navel-back-top no-print ${showBackToTop ? 'visible' : ''}`}>
        <button
          className="navel-btn-icon"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{ width: 44, height: 44, background: 'var(--color-accent)', color: '#fff', boxShadow: 'var(--shadow-float)' }}
          aria-label="Back to top"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      </div>
    </div>
  );
}

/* ---- Map Styles ---- */
const darkMapStyle = [
  { elementType: 'geometry', stylers: [{ color: '#142220' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0D1B19' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#6B8A82' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1E302D' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#263836' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#7A8F8B' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0A1614' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3D5551' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#5A7A73' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#1A2E2B' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#1A2927' }] },
];

const lightMapStyle = [
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#D4E4DE' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#F0EBE3' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#FDFBF7' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#D8E8D2' }] },
];
